import { useEffect, useRef, useState } from 'react';
import { CircleMarker, Polyline, Tooltip, useMap } from 'react-leaflet';
import { pathLength, distance } from '../lib/geo';
import { LINE_TOOLS, isPathTool } from '../lib/lineStyles';
import { formatDistance } from '../lib/utils';

const CLOSE_COLOR = '#22c55e';
const MEASURE_COLOR = '#3b82f6';

const toLatLng = (p) => [p.lat, p.lng];

// Vorschau-Position für den nächsten Punkt: der Mauszeiger. Touch-Geräte haben keinen Zeiger,
// dort gibt es keine Vorschau – die Punkte werden direkt angetippt.
const useCursorPosition = (enabled) => {
  const map = useMap();
  const [position, setPosition] = useState(null);

  useEffect(() => {
    setPosition(null);
    if (!enabled) return undefined;

    const update = (e) => setPosition(e.latlng);
    const clear = () => setPosition(null);
    map.on('mousemove', update);
    map.on('mouseout', clear);
    return () => {
      map.off('mousemove', update);
      map.off('mouseout', clear);
    };
  }, [map, enabled]);

  return position;
};

// Gesetzter Punkt mit fester Größe in Pixeln. Der Punkt, der die Zeichnung abschließt
// (erster Punkt einer Fläche, letzter einer Linie), bekommt eine große, unsichtbare Tippfläche.
const Vertex = ({ point, color, fillColor, onFinish }) => (
  <>
    <CircleMarker
      center={toLatLng(point)}
      radius={onFinish ? 8 : 6}
      interactive={false}
      pathOptions={{ color: onFinish ? '#ffffff' : color, weight: onFinish ? 3 : 2, fillColor, fillOpacity: 1 }}
    />
    {onFinish && (
      <CircleMarker
        center={toLatLng(point)}
        radius={20}
        className="drawing-vertex"
        bubblingMouseEvents={false}
        pathOptions={{ stroke: false, fillColor, fillOpacity: 0 }}
        eventHandlers={{ click: onFinish }}
      />
    )}
  </>
);

// Mit der Maus schließt ein Doppelklick die Linie/Fläche ab (auf Touch nicht, dort tippt man oft schnell)
const useDoubleClickFinish = (enabled, onFinish) => {
  const map = useMap();
  const finishRef = useRef(onFinish);
  finishRef.current = onFinish;
  useEffect(() => {
    if (!enabled) return undefined;
    const handle = () => finishRef.current?.();
    map.on('dblclick', handle);
    return () => map.off('dblclick', handle);
  }, [map, enabled]);
};

export const DrawingLayer = ({
  activeTool, drawingPoints, measurePoints, cursorPreview, onFinishDrawing, drawColor = '#ef4444'
}) => {
  const isDrawing = isPathTool(activeTool);
  const isMeasuring = activeTool === 'measure';
  const preview = useCursorPosition((isDrawing || isMeasuring) && cursorPreview);
  useDoubleClickFinish(isDrawing && cursorPreview, onFinishDrawing);

  const lastDrawn = drawingPoints[drawingPoints.length - 1];
  const lastMeasured = measurePoints[measurePoints.length - 1];
  // Ohne Mauszeiger beginnt die schließende Kante der Fläche am letzten Punkt
  // und der Messwert steht am letzten Punkt
  const closingFrom = preview || (!cursorPreview && drawingPoints.length >= 2 ? lastDrawn : null);
  const labelAt = preview || (!cursorPreview && measurePoints.length >= 2 ? lastMeasured : null);
  const finishIndex = activeTool === 'polygon' && drawingPoints.length >= 3 ? 0
    : LINE_TOOLS.includes(activeTool) && drawingPoints.length >= 2 ? drawingPoints.length - 1
    : -1;

  return (
    <>
      {/* Bereits gesetzte Punkte der Linie/Fläche */}
      {drawingPoints.length >= 2 && (
        <Polyline
          positions={drawingPoints.map(toLatLng)}
          interactive={false}
          pathOptions={{ color: drawColor, weight: 3, dashArray: '10, 10' }}
        />
      )}

      {/* Vorschau vom letzten Punkt zum Mauszeiger bzw. Fadenkreuz */}
      {isDrawing && lastDrawn && preview && (
        <Polyline
          positions={[toLatLng(lastDrawn), toLatLng(preview)]}
          interactive={false}
          pathOptions={{ color: drawColor, weight: 2, dashArray: '5, 5', opacity: 0.7 }}
        />
      )}

      {/* Fläche: Vorschau der schließenden Kante zurück zum ersten Punkt */}
      {activeTool === 'polygon' && drawingPoints.length >= 2 && closingFrom && (
        <Polyline
          positions={[toLatLng(closingFrom), toLatLng(drawingPoints[0])]}
          interactive={false}
          pathOptions={{ color: CLOSE_COLOR, weight: 2, dashArray: '5, 5', opacity: 0.5 }}
        />
      )}

      {drawingPoints.map((point, index) => (
        <Vertex
          key={`draw-point-${index}-${index === finishIndex}`}
          point={point}
          color={drawColor}
          fillColor={index === 0 ? CLOSE_COLOR : drawColor}
          onFinish={index === finishIndex ? onFinishDrawing : undefined}
        />
      ))}

      {/* Messen */}
      {measurePoints.length >= 2 && (
        <Polyline
          positions={measurePoints.map(toLatLng)}
          interactive={false}
          pathOptions={{ color: MEASURE_COLOR, weight: 3, dashArray: '5, 10' }}
        />
      )}

      {isMeasuring && lastMeasured && preview && (
        <Polyline
          positions={[toLatLng(lastMeasured), toLatLng(preview)]}
          interactive={false}
          pathOptions={{ color: MEASURE_COLOR, weight: 2, dashArray: '3, 6', opacity: 0.7 }}
        />
      )}

      {/* Gesamtstrecke am Mauszeiger bzw. (Touch) am letzten Punkt */}
      {isMeasuring && lastMeasured && labelAt && (
        <CircleMarker
          key={cursorPreview ? 'cursor' : 'last-point'}
          center={toLatLng(labelAt)}
          radius={0}
          interactive={false}
          pathOptions={{ stroke: false, fill: false }}
        >
          {/* Am letzten Punkt (Touch) auf die Seite mit mehr Platz, damit es am Rand nicht abgeschnitten wird */}
          <Tooltip permanent direction={cursorPreview ? 'right' : 'auto'} offset={[16, 0]} className="measure-label">
            {formatDistance(pathLength(measurePoints) + (preview ? distance(lastMeasured, preview) : 0))}
          </Tooltip>
        </CircleMarker>
      )}

      {measurePoints.map((point, index) => (
        <Vertex key={`measure-point-${index}`} point={point} color={MEASURE_COLOR} fillColor={MEASURE_COLOR} />
      ))}
    </>
  );
};

export default DrawingLayer;
